import { sortAsc } from '@strata/helper-functions';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { DetailLabel } from '@strata/react-label-value-pairs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Tag } from '@strata/react-tags';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import classNames from 'classnames';
import { FieldArray, Formik, getIn } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { handleError, useLastErrorDialog } from 'services/ErrorManagement';
import { get, put, useGet } from 'services/Http';

const lddCapabilitySort = (a, b) => sortAsc(a.name, b.name);

const getDrugDisplayName = (drug) => {
    let brandGenericDescription = '';
    if (drug?.description !== drug?.brand_product_description) {
        brandGenericDescription += drug?.brand_product_description ?? '';
    }
    if (drug?.description !== drug?.generic_product_description) {
        if (brandGenericDescription.length > 0) {
            brandGenericDescription += '/';
        }
        brandGenericDescription += drug?.generic_product_description ?? '';
    }
    return `${drug?.description}${
        brandGenericDescription.length > 0 ? ` (${brandGenericDescription})` : ''
    }`;
};

const DrugVersion = {
    BRAND: 1,
    GENERIC: 2,
    EITHER: 3,
};

const DrugVersionName = {
    [DrugVersion.BRAND]: 'Brand',
    [DrugVersion.GENERIC]: 'Generic',
    [DrugVersion.EITHER]: 'Either',
};

const DrugVersionOptions = Object.entries(DrugVersionName).map(([key, name]) => ({
    name,
    value: Number(key),
}));

export const LddCapabilityTab = ({ onSecondaryButtonClick = undefined }) => {
    const { npi } = useParams();

    const { ErrorDialogComponent: ErrorDialog, parseAndSetLastError } = useLastErrorDialog();

    const [existingCapabilities, setExistingCapabilities] = useState();
    useEffect(() => {
        void (async () => {
            try {
                setExistingCapabilities(await get(`/reference/capabilities/${npi}`));
            } catch (err) {
                handleError('Error fetching state capabilities', err);
            }
        })();
    }, [npi]);

    const drugData = useGet(`/reference/product_benefit_configs/?only_ldd=true`);

    const drugCapabilityOptions = useMemo(
        () =>
            (drugData ?? [])
                .map((d) => ({
                    name: getDrugDisplayName(d),
                    secondaryData: d.oid,
                    oid: d.oid,
                }))
                .sort(lddCapabilitySort),
        [drugData],
    );

    const mappedCapabilities = useMemo(
        () =>
            (existingCapabilities?.drug_capabilities ?? []).map((cap) => ({
                name: drugCapabilityOptions.find((d) => d.oid === cap.product_service_oid)?.name,
                value: {
                    product_service_oid: cap.product_service_oid,
                    drug_version: cap.drug_version,
                },
            })),
        [drugCapabilityOptions, existingCapabilities?.drug_capabilities],
    );

    return (
        <Formik
            initialValues={{
                drugCapabilityList: mappedCapabilities,
                drugsToRemoveList: [],
            }}
            validationSchema={Yup.object({
                drugCapabilityList: Yup.array().required(
                    validationStrings.requiredField.errorMessage,
                ),
                drugsToRemoveList: Yup.array().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            enableReinitialize
            onSubmit={async (values) => {
                try {
                    const body = {
                        supplier_npi: npi,
                        cap_type: 'drug',
                        caps_to_add: values.drugCapabilityList.map((cap) => cap.value),
                        caps_to_remove: values.drugsToRemoveList.map((cap) => ({
                            product_service_oid: cap,
                        })),
                    };
                    await put(`/reference/capabilities/${npi}`, body);

                    await SuccessToast({
                        message: 'LDD Capabilities successfully updated.',
                    });

                    setExistingCapabilities(await get(`/reference/capabilities/${npi}`));
                } catch (err) {
                    parseAndSetLastError(err);
                }
            }}
        >
            {(formik) => {
                const remainingDrugs = new Set(
                    drugCapabilityOptions
                        .map((d) => d.oid)
                        .filter(
                            (d) =>
                                !formik.values.drugCapabilityList.find(
                                    (cap) => d === cap.value.product_service_oid,
                                ),
                        ),
                );
                const drugToAdd = getIn(formik.values, 'drugCapability');
                const drugVersionToAdd = getIn(formik.values, 'drugVersion');
                const clearAllDrugs = () => {
                    formik.setValues({
                        ...formik.values,
                        drugCapability: undefined,
                        drugVersion: undefined,
                        drugCapabilityList: [],
                        drugsToRemoveList: (formik.initialValues.drugCapabilityList ?? []).map(
                            (cap) => cap.value.product_service_oid,
                        ),
                    });
                };

                return (
                    <>
                        <ErrorDialog />
                        <FormContainer
                            formTitle="Edit LDD Capabilities"
                            isLoading={!(existingCapabilities && drugData)}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={!(formik.dirty && formik.isValid) || formik.isSubmitting}
                            onSecondaryButtonClick={onSecondaryButtonClick ?? formik.handleReset}
                            formLeftContent={
                                <>
                                    <div
                                        className="flex__gap--small"
                                        style={{ alignItems: 'flex-start' }}
                                    >
                                        <SuggestSingleSelectFormik
                                            id="drugCapability"
                                            label="Drug"
                                            required
                                            disableAutoPopulate
                                            helperText="A LD drug that the supplier has access to"
                                            values={drugCapabilityOptions.filter((d) =>
                                                remainingDrugs.has(d.oid),
                                            )}
                                        />
                                        <SuggestSingleSelectFormik
                                            id="drugVersion"
                                            label="Version"
                                            required
                                            disableAutoPopulate
                                            helperText="Whether the drug is brand, generic, or either"
                                            values={DrugVersionOptions}
                                        />
                                        <Button
                                            label="Add Drug"
                                            className="sds_form-button__center-with-inputs"
                                            disabled={!(drugToAdd && drugVersionToAdd)}
                                            intent={Button.Intent.PRIMARY}
                                            onClick={() => {
                                                const productConfig = drugCapabilityOptions.find(
                                                    (d) => d.oid === drugToAdd.oid,
                                                );
                                                // Prevent same capability from being added twice
                                                if (
                                                    !formik.values.drugCapabilityList?.find(
                                                        (currentDrug) =>
                                                            currentDrug.value
                                                                .product_service_oid ===
                                                            drugToAdd.oid,
                                                    )
                                                ) {
                                                    formik.setValues({
                                                        ...formik.values,
                                                        drugCapability: undefined,
                                                        drugVersion: undefined,
                                                        drugCapabilityList: [
                                                            {
                                                                name: productConfig.name,
                                                                value: {
                                                                    product_service_oid:
                                                                        productConfig.oid,
                                                                    drug_version:
                                                                        drugVersionToAdd?.value,
                                                                },
                                                            },
                                                            ...formik.values.drugCapabilityList,
                                                        ],
                                                        drugsToRemoveList:
                                                            formik.values.drugsToRemoveList.filter(
                                                                (currentDrugToRemove) =>
                                                                    currentDrugToRemove !==
                                                                    drugToAdd.oid,
                                                            ),
                                                    });
                                                }
                                            }}
                                        />
                                        <ActionButtonText
                                            label="Clear All"
                                            className="sds_form-button__center-with-inputs"
                                            style={{ marginLeft: 'auto' }}
                                            disabled={formik.values.drugCapabilityList.length === 0}
                                            onClick={clearAllDrugs}
                                        />
                                    </div>
                                    <DetailLabel label="Selected Drugs" />
                                    <FieldArray name="drugCapabilityList">
                                        {(fieldArray) => (
                                            <div className="sds_capabilities">
                                                {formik.values.drugCapabilityList
                                                    .sort(lddCapabilitySort)
                                                    .map((drugCapability, index) => {
                                                        const {
                                                            product_service_oid: oid,
                                                            drug_version: drugVersion,
                                                        } = drugCapability.value;
                                                        return (
                                                            <Tag
                                                                key={oid}
                                                                className={classNames(
                                                                    'margin__bottom--medium',
                                                                    'sds_capabilities__tag',
                                                                )}
                                                                label={`${drugCapability.name} - ${oid} - ${DrugVersionName[drugVersion]}`}
                                                                onRemove={async () => {
                                                                    const wasInInitialValues =
                                                                        formik.initialValues.drugCapabilityList.find(
                                                                            (cap) =>
                                                                                cap.value
                                                                                    .product_service_oid ===
                                                                                oid,
                                                                        );
                                                                    if (wasInInitialValues) {
                                                                        await formik.setFieldValue(
                                                                            'drugsToRemoveList',
                                                                            [
                                                                                ...formik.values
                                                                                    .drugsToRemoveList,
                                                                                oid,
                                                                            ],
                                                                        );
                                                                    }
                                                                    fieldArray.remove(index);
                                                                }}
                                                            />
                                                        );
                                                    })}
                                            </div>
                                        )}
                                    </FieldArray>
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};

LddCapabilityTab.propTypes = {
    onSecondaryButtonClick: PropTypes.func,
};
